import { Spin } from 'antd';
import { useParams } from 'react-router';
import CardSection from '../../../../components/CardSection';
import { PageHeaderWithBackButton } from '../../../../components/common';
import useSetBreadCrumbParams from '../../../../hooks/useSetBreadCrumbParams';
import { useFetchFavePayDetails } from '../../../../services/MySubmissions';
import {
  customBooleanValue,
  dateOrDash,
  valueOrDash,
} from '../../../../utils/utilFunctions';
import { useFetchFlipper } from '../../../../services/useFetchFlipper';
import { useTenantContext } from '../../../../contexts/TenantContext';
import { useMemo } from 'react';

const FavePaySubmissionsDetailsView = () => {
  const { id } = useParams();
  const { isMY: isMy, isSG: isSg, tenantCurrency } = useTenantContext();
  const favepayID = String(id);

  const { data: favepaySubmissionsData, isFetching } = useFetchFavePayDetails({
    id: favepayID,
  });
  const { data: isDisabledTransactionFeeSg, isFetching: isFetchingSgFlipper } =
    useFetchFlipper({
      key: 'disable_merchant_connect_favepay_transaction_fee_singapore',
    });
  const { data: isDisabledTransactionFeeMy, isFetching: isFetchingMyFlipper } =
    useFetchFlipper({
      key: 'disable_merchant_connect_favepay_transaction_fee_kuala-lumpur',
    });

  const isLoading = isFetching || isFetchingSgFlipper || isFetchingMyFlipper;

  const isNotEmpty = favepaySubmissionsData !== undefined;
  const hasTransactionFee = useMemo(() => {
    if (!isLoading) {
      return (
        (!!!isDisabledTransactionFeeSg?.value && isSg) ||
        (!!!isDisabledTransactionFeeMy?.value && isMy)
      );
    }
    return false;
  }, [
    isLoading,
    isDisabledTransactionFeeSg,
    isDisabledTransactionFeeMy,
    isSg,
    isMy,
  ]);

  useSetBreadCrumbParams(
    [
      { label: '', key: 'favepay' },
      {
        label: `${String(
          favepaySubmissionsData?.company?.name,
        )} (FavePay Detail)`,
        key: String(id),
      },
      { label: '', key: 'details' },
    ],
    isNotEmpty,
  );

  const favePayDescriptions = [
    {
      key: '1',
      label: 'Cashback %',
      children: valueOrDash(favepaySubmissionsData?.cashback_rate, '%'),
    },
    {
      key: '2',
      label: 'Fave Commission %',
      children: valueOrDash(favepaySubmissionsData?.fee_percentage, '%'),
    },
    hasTransactionFee && {
      key: '3',
      label: 'Opt-In Transaction Fee',
      children: customBooleanValue(favepaySubmissionsData?.has_transaction_fee),
    },
    hasTransactionFee && {
      key: '4',
      label: 'Transaction Fee',
      children: favepaySubmissionsData?.transaction_fee
        ? `${tenantCurrency}${favepaySubmissionsData?.transaction_fee}`
        : '-',
    },
    {
      key: '5',
      label: 'Fave Commission Effective Date',
      children: dateOrDash(favepaySubmissionsData?.fave_fee_effective_date),
    },
    {
      key: '6',
      label: 'Onboarding Status',
      children: valueOrDash(favepaySubmissionsData?.onboarding_type),
    },
    {
      key: '7',
      label: 'Activate FavePay upon approval?',
      children: customBooleanValue(favepaySubmissionsData?.has_fave_payment),
    },
    {
      key: '8',
      label: 'FavePay Live date',
      children: dateOrDash(
        favepaySubmissionsData?.scheduled_favepay_enable_date,
      ),
    },
    {
      key: '9',
      label: 'Undiscounted Description',
      children: valueOrDash(favepaySubmissionsData?.undiscounted_description),
    },
    {
      key: '10',
      label: 'Remarks to Operations',
      children: valueOrDash(favepaySubmissionsData?.remarks),
    },
    // filters out undefined values
  ].filter(Boolean) as { key: string; label: string; children: string }[];

  const aliPayDescriptions = [
    {
      key: '1',
      label: 'Merchant accepts AliPay?',
      children: customBooleanValue(favepaySubmissionsData?.alipay_opt_in),
    },
    {
      key: '2',
      label: 'AliPay Category',
      children: valueOrDash(favepaySubmissionsData?.alipay_category?.name),
    },
    {
      key: '3',
      label: 'AliPay Discount %',
      children: valueOrDash(favepaySubmissionsData?.alipay_discount_percentage),
    },
    {
      key: '4',
      label: 'AliPay Fee %',
      children: valueOrDash(favepaySubmissionsData?.alipay_fee_percentage),
    },
    {
      key: '5',
      label: 'AliPay Fee Effective Date',
      children: dateOrDash(favepaySubmissionsData?.alipay_fee_effective_date),
    },
    {
      key: '6',
      label: 'Operating Days & Hours',
      children: valueOrDash(favepaySubmissionsData?.business_hours),
    },
  ];

  const applyToOutletsDescriptions = [
    {
      key: '1',
      label: 'Apply to Outlet',
      children: valueOrDash(favepaySubmissionsData?.outlet?.name),
    },
  ];

  const favepaySections = [
    {
      key: '1',
      title: 'FavePay Detail',
      data: favePayDescriptions,
    },
    {
      key: '2',
      title: 'AliPay Detail',
      data: aliPayDescriptions,
    },
    {
      key: '3',
      title: 'Apply to Outlet',
      data: applyToOutletsDescriptions,
    },
  ];

  return (
    <div>
      <PageHeaderWithBackButton
        title={valueOrDash(favepaySubmissionsData?.company?.name)}
        subTitle={'FavePay Detail'}
      />
      <Spin spinning={isLoading} style={{ width: '100%' }}>
        {favepaySections.map(({ key, title, data }) => (
          <CardSection
            key={key}
            sectionNumber={key}
            title={title}
            data={data}
          />
        ))}
      </Spin>
    </div>
  );
};

export default FavePaySubmissionsDetailsView;
